/* Copyright (c) 2024 Xperi. All Rights Reserved.  */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import {
    SessionCreateDeviceTypeEnum,
    SessionCreateDrmTypeEnum,
    SessionCreateTransportEncodingTypeEnum,
} from 'client-code/session-manager';

import { DeviceModel } from '../../device';
import { ErrorTypes } from '../../error';
import { Log, VuLogger } from '../../logging';
import { RetryMechanism } from '../../retrymechanism/RetryMechanism';
import { RetryOptions } from '../../retrymechanism/RetryOptions';
import { TSession } from '../../types';
import { TCreateSessionParams } from '../../types/TCreateSessionParams';
import { UniqueIdentifier } from '../../utils';
import { SessionState } from '../enums';
import { ISessionCreateModel } from '../interfaces';
import { StreamingSessionImpl } from './StreamingSessionImpl';

export class NdvrStreamingSessionImpl extends StreamingSessionImpl {
    private static TAG: string = 'NdvrStreamingSessionImpl';

    private static oINdvrStreamingSession: NdvrStreamingSessionImpl;

    public static getInstance(): NdvrStreamingSessionImpl {
        if (!NdvrStreamingSessionImpl.oINdvrStreamingSession)
            NdvrStreamingSessionImpl.oINdvrStreamingSession = new NdvrStreamingSessionImpl();
        return NdvrStreamingSessionImpl.oINdvrStreamingSession;
    }

    public createStreamingSession(sessionCreateModel: ISessionCreateModel): Promise<void> {
        const { transportEncodingType, recordingWatchAction } = sessionCreateModel;
        const { partnerStationId, recordingProviderPartnerId, recordingId } =
            recordingWatchAction?.kernel || {};

        return new Promise((resolve, reject) => {
            if (!recordingProviderPartnerId && !recordingId && !partnerStationId) {
                this.mError = this.getPlaybackError(
                    ErrorTypes.LIVE_TV_DEVICE_ERROR_MISSING_CONFIG_V904,
                );
                this.mSessionState = SessionState.ERROR;
                return reject();
            }
            // TODO: Need to update devicetype, drmtype, transportEncodingType based on actual data
            const sessionParams: TCreateSessionParams = {
                deviceType: SessionCreateDeviceTypeEnum.IptvSmartTv,
                drmType: SessionCreateDrmTypeEnum.Widevine,
                partnerId: recordingProviderPartnerId || '',
                partnerRecordingId: recordingId || '',
                partnerServicesCustomerId: DeviceModel.getTiVoSettings().getPartnerCustomerId(),
                partnerStationId: partnerStationId || '',
                transportEncodingType: <SessionCreateTransportEncodingTypeEnum>(
                    transportEncodingType
                ),
            };

            const createSession = this.mServices.createSession.bind(this.mServices);
            // TODO: need to check if retry is needed.
            RetryMechanism.callServiceWithRetry<TSession>(
                createSession,
                [sessionParams, UniqueIdentifier.getNextSessionNetworkCallId()],
                <RetryOptions>this.SESSION_MANAGER_DEFAULT_RETRY_OPTIONS,
            )
                .then((response: TSession) => {
                    if (
                        response.sessionNetworkCallId === UniqueIdentifier.getSessionNetworkCallId()
                    ) {
                        this.oSession = response.session;
                        VuLogger.setSessionId(this.oSession.sessionId);
                        this.mSessionState = SessionState.SUCCESS;
                        resolve();
                    }
                })
                .catch((error: any) => {
                    // service is unreachable
                    if (error.sessionNetworkCallId === UniqueIdentifier.getSessionNetworkCallId()) {
                        this.mError = this.getSessionError(error, true);
                        Diagnostics.error(
                            NdvrStreamingSessionImpl.TAG,
                            `{createStreamingSession: ${error}}`,
                        );
                        Log.e(NdvrStreamingSessionImpl.TAG, `{createStreamingSession: ${error}}`);
                        this.mSessionState = SessionState.ERROR;
                        reject();
                    }
                });
        });
    }
}
